import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Plus, Search } from 'lucide-react';

export interface AgentCatalogHeaderProps {
    searchQuery: string;
    onSearchChange: (value: string) => void;
    onAddAgent: () => void;
    isLoading?: boolean;
}

export const AgentCatalogHeader: React.FC<AgentCatalogHeaderProps> = ({
    searchQuery,
    onSearchChange,
    onAddAgent,
    isLoading = false,
}) => {
    const { t } = useTranslation();
    const [localValue, setLocalValue] = useState(searchQuery);

    useEffect(() => {
        setLocalValue(searchQuery);
    }, [searchQuery]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value);
    };

    const handleSearch = () => {
        if (localValue !== searchQuery) {
            onSearchChange(localValue);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <div className="flex items-center gap-[14px] mb-7 max-md:mb-5">
            <div className="h-[29px] bg-card shadow-custom flex items-center justify-between gap-2.5 rounded-md py-1.5 pr-2 pl-[7px] w-[178px]">
                <input
                    value={localValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={t('agent.catalog.search.placeholder')}
                    className="flex-1 w-full bg-card outline-0 text-xs"
                    disabled={isLoading}
                />
                <button
                    type="button"
                    onClick={handleSearch}
                    disabled={isLoading}
                    className="flex items-center justify-center"
                    tabIndex={0}
                    aria-label={t('agent.catalog.search.ariaLabel')}
                >
                    {isLoading ? (
                        <div className="w-4 h-4 animate-spin rounded-full border-2 border-border border-t-primary" />
                    ) : (
                        <Search className="w-4 h-4" />
                    )}
                </button>
            </div>
            <Button
                className="flex items-center gap-1.5 text-xs h-[29px] rounded-[6px] pr-[10px] pl-3"
                size="xs"
                onClick={onAddAgent}
                disabled={isLoading}
            >
                <Plus className="w-4 h-4" /> {t('agent.catalog.actions.addAgent')}
            </Button>
        </div>
    );
};
